import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { authHeader } from "../../_api/apiUtils";
import { SaveUploadReport } from "../../_api/documentsApi";
import { loadVehicle } from "@/_redux/actions/vehicleActions";
import { ConfirmationDialog } from "@/_components";
import SaveOverlay from "../../_components/SaveOverlay";
import { langConversionHelper } from "@/_helpers";
import { signaturesReadOnly } from "./FormHelpers";
import FormHeader from "./FormHeader";
import "./fillableFormStyles.css";

import {
  PdfViewerComponent,
  Toolbar,
  Magnification,
  Navigation,
  LinkAnnotation,
  BookmarkView,
  ThumbnailView,
  Print,
  TextSelection,
  TextSearch,
  Annotation,
  FormFields,
  FormDesigner,
  Inject,
} from "@syncfusion/ej2-react-pdfviewer";

function FillableForm({
  formName,
  templateFile,
  validationFunction,
  fillingFunction,
  fillingData,
  handleClose,
  handleFormSaved,
  userID,
  clientID,
  jurisdictionID,
  reportID,
  vehicleID,
  appointmentID,
  vehicle,
  loadVehicle,
  formDescription,
  handleSignNowSubmit,
}) {
  const { t, i18n } = useTranslation();
  const isMountedRef = useRef(null);
  let viewer;
  const serviceUrl = process.env.API_URL + "/PdfViewer";
  const token = authHeader(serviceUrl).Authorization;
  const [showSaving, setShowSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showError, setShowError] = useState(false);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    isMountedRef.current = true;

    // Create the Vehicle Obj
    if (vehicle === undefined || vehicle[0].CurrentState === "Undefined") {
      if (isMountedRef.current) {
        loadVehicle(clientID, userID).catch((error) => {
          console.log("Loading vehicle failed! " + error);
        });
      }
    }

    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (
      vehicleID > 0 &&
      vehicle !== null &&
      vehicle !== undefined &&
      vehicle[0].CurrentState !== "Undefined"
    ) {
      let vehicleObj = vehicle.find((x) => x.vehicleId === vehicleID);
      let display = {
        displayInfo:
          (vehicleObj.color.length > 0
            ? langConversionHelper.individualColorConversion(
                vehicleObj.color,
                i18n.language.slice(0, 2)
              ) + ` `
            : ``) +
          ` ${vehicleObj.make}` +
          ` ${vehicleObj.model}`,
      };

      let updatedVehicle = { ...vehicleObj, ...display };

      setSelectedVehicle(updatedVehicle);
    }
  }, [vehicle]);

  const handleDocumentLoaded = () => {
    testFill();
    setDocumentLoaded(true);
  };

  return (
    <div>
      <Grid container alignItems="center" justifyContent="center">
        <Grid container direction="column">
          {showSaving && !showError && (
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              style={{ display: showSaving === true ? "inherit" : "none" }}
            >
              <Grid item style={{ padding: 5 }}>
                <SaveOverlay
                  open={showSaving}
                  setOpen={setShowSaving}
                ></SaveOverlay>
              </Grid>
            </Grid>
          )}
          {showError && <></>}
          <Grid item>
            <FormHeader
              formName={formDescription}
              vehicleInfo={
                selectedVehicle === null || selectedVehicle === undefined
                  ? ""
                  : selectedVehicle.displayInfo
              }
              save={saveForm}
              cancel={handleClose}
              submitting={showSaving || documentLoaded == false}
            />
          </Grid>
          <Grid item>
            {!showSaved && (
              <PdfViewerComponent
                id="container"
                ref={(scope) => {
                  viewer = scope;
                }}
                documentPath={templateFile}
                serviceUrl={serviceUrl}
                ajaxRequestSettings={{
                  ajaxHeaders: [
                    { headerName: "Authorization", headerValue: token },
                  ],
                }}
                documentLoad={handleDocumentLoaded}
                showNotificationDialog={false}
                enableToolbar={false}
                enableNavigationToolbar={false}
                disabled={showSaving}
              >
                <Inject
                  services={[
                    Toolbar,
                    Magnification,
                    Navigation,
                    LinkAnnotation,
                    BookmarkView,
                    ThumbnailView,
                    Print,
                    TextSelection,
                    TextSearch,
                    Annotation,
                    FormFields,
                    FormDesigner,
                  ]}
                />
              </PdfViewerComponent>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ConfirmationDialog
        title={t("documents.declineModalHeader")}
        open={showDeclineModal}
        setOpen={setShowDeclineModal}
        confirmBtnLabel={t("documents.signButton")}
        cancelBtnLabel={t("documents.declineButton")}
        onConfirm={() => {
          setShowDeclineModal(false);
        }}
        onCancel={() => {
          setShowDeclineModal(false);
          handleClose();
        }}
        showHelp={true}
      >
        {t("documents.declineModalBody")}
      </ConfirmationDialog>
    </div>
  );

  function testFill() {
    fillingFunction(fillingData, viewer);
  }

  function saveForm() {
    // clear error text
    setShowSaved(false);
    setShowSaving(true);
    setShowError(false);

    // validate data
    let formIsValid = validationFunction(viewer);
    if (formIsValid === true) {
      // mark all fields as read only
      signaturesReadOnly(viewer);
      // send form to save
      viewer
        .saveAsBlob()
        .then((blob) => {
          var formData = new FormData();
          formData.append("uploadFile", blob, formName);
          SaveUploadReport(
            userID,
            clientID,
            jurisdictionID,
            reportID,
            vehicleID,
            appointmentID,
            true, // flattenForm
            formData // document
          )
            .then(() => {
              setShowSaved(true);
              setShowSaving(false);
              handleFormSaved();
              if (handleSignNowSubmit) {
                handleSignNowSubmit();
              }
            })
            .catch((error) => {
              console.error("Error uploading document : ", error);
              setShowError(true);
              toast.error(
                t("documents.saveError") + ": " + t("documents.uploadError")
              );
            });
        })
        .catch((error) => {
          console.error("Error saving pdf as blob : ", error);
          setShowError(true);
          toast.error(t("document.saveError"));
        });
    } else if (formIsValid === "declined") {
      setShowSaving(false);
      setShowDeclineModal(true);
    } else {
      setShowError(true);
      setShowSaving(false);
      if (formIsValid === false || formIsValid === "") {
        toast.error(t("documents.formNotValid"));
      } else {
        toast.error(formIsValid);
      }
    }
  }
}

FillableForm.propTypes = {
  formName: PropTypes.string.isRequired,
  formDescription: PropTypes.string.isRequired,
  templateFile: PropTypes.string.isRequired,
  validationFunction: PropTypes.func.isRequired,
  fillingFunction: PropTypes.func.isRequired,
  fillingData: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleFormSaved: PropTypes.func.isRequired,
  userID: PropTypes.number.isRequired,
  clientID: PropTypes.number.isRequired,
  jurisdictionID: PropTypes.number.isRequired,
  reportID: PropTypes.number.isRequired,
  vehicleID: PropTypes.number.isRequired,
  appointmentID: PropTypes.number.isRequired,
  vehicle: PropTypes.array,
  loadVehicle: PropTypes.func,
  vehicleMake: PropTypes.array,
  handleSignNowSubmit: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    vehicle: state.vehicle,
    vehicleMake: state.vehicleMake,
  };
}

const mapDispatchToProps = {
  loadVehicle,
};

export default connect(mapStateToProps, mapDispatchToProps)(FillableForm);
